import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Collections as SectionIcon,
  LocalBar as WineIcon,
  AddCircle as AddWineIcon,
  RemoveCircle as RemoveWineIcon,
  KeyboardArrowUp as MoveUpIcon,
  KeyboardArrowDown as MoveDownIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import BackButton from '../../components/common/BackButton';
import { firebaseSectionService } from '../../services/firebaseSectionService';
import { firebaseWinesService, firebaseCategoriesService } from '../../services/firebaseService';

const AdminSections = () => {
  const [open, setOpen] = useState(false);
  const [wineDialog, setWineDialog] = useState({ open: false, section: null });
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'featured', // featured, winery, recommendations, etc.
    maxWines: 6,
    isActive: true
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, section: null });

  const queryClient = useQueryClient();

  // Query para obtener secciones (combinando secciones personalizadas y categorías)
  const { data: sections = [], isLoading, error } = useQuery({
    queryKey: ['firebaseSections'],
    queryFn: async () => {
      try {
        // Obtener secciones personalizadas
        const customSections = await firebaseSectionService.getAllSections();
        console.log('Secciones personalizadas:', customSections);
        
        // Obtener categorías existentes
        const categoriesResponse = await firebaseCategoriesService.getAll();
        const categories = categoriesResponse.data.data || [];
        console.log('Categorías obtenidas:', categories);
        
        // Convertir categorías a formato de sección
        const categorySections = categories
          .filter(category => category && category.id) // Filtrar categorías válidas
          .map((category, index) => ({
            id: `category-${category.id}`,
            name: category.name,
            description: category.description,
            type: 'category',
            maxWines: 10,
            isActive: true,
            wineIds: [], // Se calculará dinámicamente
            isCategory: true,
            originalCategoryId: category.id,
            order: customSections.length + index + 1, // Orden después de las secciones personalizadas
            createdAt: category.createdAt || new Date()
          }));
        
        // Combinar secciones personalizadas con categorías
        const allSections = [...customSections, ...categorySections];
        console.log('Todas las secciones:', allSections);
        return allSections;
      } catch (error) {
        console.error('Error al obtener secciones:', error);
        return [];
      }
    }
  });

  // Query para obtener vinos (para el selector)
  const { data: wines = [] } = useQuery({
    queryKey: ['firebaseWines'],
    queryFn: async () => {
      const winesData = await firebaseWinesService.getAll();
      // El servicio de Firebase retorna { data: { data: [...] } }
      return winesData.data.data;
    }
  });

  // Función para obtener vinos de una categoría específica
  const getWinesByCategory = (categoryId) => {
    const categoryWines = wines.filter(wine => wine.categoryId === categoryId);
    console.log(`Vinos en categoría ${categoryId}:`, categoryWines);
    return categoryWines;
  };

  // Función para obtener vinos de una sección
  const getWinesBySection = (section) => {
    if (!section) return [];
    
    if (section.isCategory) {
      const categoryWines = getWinesByCategory(section.originalCategoryId);
      console.log(`Sección categoría ${section.name}:`, categoryWines.length, 'vinos');
      return categoryWines;
    } else {
      const sectionWines = wines.filter(wine => section.wineIds?.includes(wine.id));
      console.log(`Sección personalizada ${section.name}:`, sectionWines.length, 'vinos de', section.wineIds?.length || 0, 'IDs');
      return sectionWines;
    }
  };

  // Mutation para crear sección
  const createMutation = useMutation({
    mutationFn: async (data) => {
      return await firebaseSectionService.createSection(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseSections']);
      setOpen(false);
      resetForm();
      toast.success('Sección creada exitosamente');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al crear la sección');
    }
  });

  // Mutation para actualizar sección
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return await firebaseSectionService.updateSection(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseSections']);
      setOpen(false);
      resetForm();
      toast.success('Sección actualizada exitosamente');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al actualizar la sección');
    }
  });

  // Mutation para eliminar sección
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return await firebaseSectionService.deleteSection(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseSections']);
      setDeleteDialog({ open: false, section: null });
      toast.success('Sección eliminada exitosamente');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al eliminar la sección');
    }
  });

  // Mutation para mover sección hacia arriba
  const moveUpMutation = useMutation({
    mutationFn: async (id) => {
      return await firebaseSectionService.moveSectionUp(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseSections']);
      toast.success('Sección movida hacia arriba');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al mover la sección');
    }
  });

  // Mutation para mover sección hacia abajo
  const moveDownMutation = useMutation({
    mutationFn: async (id) => {
      return await firebaseSectionService.moveSectionDown(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseSections']);
      toast.success('Sección movida hacia abajo');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al mover la sección');
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'featured',
      maxWines: 6,
      isActive: true
    });
    setEditingSection(null);
  };

  const handleOpenDialog = (section = null) => {
    if (section) {
      setEditingSection(section);
      setFormData({
        name: section.name,
        description: section.description,
        type: section.type,
        maxWines: section.maxWines,
        isActive: section.isActive
      });
    } else {
      resetForm();
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    if (editingSection) {
      updateMutation.mutate({ id: editingSection.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDeleteClick = (section) => {
    setDeleteDialog({ open: true, section });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.section) {
      deleteMutation.mutate(deleteDialog.section.id);
    }
  };

  const handleMoveUp = (section) => {
    if (!section?.isCategory) {
      moveUpMutation.mutate(section.id);
    }
  };

  const handleMoveDown = (section) => {
    if (!section?.isCategory) {
      moveDownMutation.mutate(section.id);
    }
  };

  const handleOpenWineDialog = (section) => {
    setWineDialog({ open: true, section });
  };

  const handleAddWineToSection = async (wineId) => {
    try {
      await firebaseSectionService.addWineToSection(wineDialog.section.id, wineId);
      queryClient.invalidateQueries(['firebaseSections']);
      toast.success('Vino añadido a la sección');
      setWineDialog({ open: false, section: null });
    } catch (error) {
      toast.error('Error al añadir el vino a la sección');
    }
  };

  const handleRemoveWineFromSection = async (wineId) => {
    try {
      await firebaseSectionService.removeWineFromSection(wineDialog.section.id, wineId);
      queryClient.invalidateQueries(['firebaseSections']);
      toast.success('Vino eliminado de la sección');
    } catch (error) {
      toast.error('Error al eliminar el vino de la sección');
    }
  };

  // Función para asignar categorías a vinos que no las tienen
  const assignCategoriesToWines = async () => {
    try {
      console.log('🔧 Asignando categorías automáticamente...');
      
      const winesWithoutCategory = wines.filter(wine => !wine.categoryId);
      console.log('⚠️ Vinos sin categoría:', winesWithoutCategory.length);
      
      if (winesWithoutCategory.length === 0) {
        toast.success('Todos los vinos ya tienen categoría asignada');
        return;
      }

      // Asignar categorías basándose en el nombre del vino
      const categoryAssignments = {
        'rioja': 'cat-rioja',
        'ribera': 'cat-ribera-del-duero',
        'ribera del duero': 'cat-ribera-del-duero',
        'duero': 'cat-ribera-del-duero',
        'penedes': 'cat-penedes',
        'jumilla': 'cat-jumilla',
        'rias baixas': 'cat-rias-baixas',
        'albariño': 'cat-rias-baixas',
        'cava': 'cat-cava',
        'champagne': 'cat-champagne',
        'prosecco': 'cat-prosecco',
        'tinto': 'cat-tintos',
        'blanco': 'cat-blancos',
        'rosado': 'cat-rosados',
        'rosé': 'cat-rosados',
        'dulce': 'cat-dulces',
        'espumoso': 'cat-espumosos',
        'jerez': 'cat-jerez',
        'sherry': 'cat-jerez'
      };

      let assignedCount = 0;
      for (const wine of winesWithoutCategory) {
        const wineName = wine.name.toLowerCase();
        let assignedCategory = null;
        
        for (const [keyword, categoryId] of Object.entries(categoryAssignments)) {
          if (wineName.includes(keyword)) {
            assignedCategory = categoryId;
            console.log(`✅ Asignando ${categoryId} a ${wine.name} (keyword: ${keyword})`);
            break;
          }
        }
        
        if (assignedCategory) {
          // Actualizar el vino en Firebase
          const updateData = new FormData();
          updateData.append('categoryId', assignedCategory);
          
          await firebaseWinesService.update(wine.id, updateData);
          assignedCount++;
        } else {
          console.log(`❌ No se pudo asignar categoría a: ${wine.name}`);
        }
      }

      console.log(`✅ Categorías asignadas a ${assignedCount} vinos`);
      toast.success(`Categorías asignadas automáticamente a ${assignedCount} vinos`);
      
      // Refrescar datos
      queryClient.invalidateQueries(['firebaseWines']);
      queryClient.invalidateQueries(['firebaseSections']);
    } catch (error) {
      console.error('❌ Error asignando categorías:', error);
      toast.error('Error al asignar categorías: ' + error.message);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'featured': return 'primary';
      case 'winery': return 'secondary';
      case 'recommendations': return 'success';
      case 'category': return 'info';
      case 'seasonal': return 'warning';
      default: return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'featured': return 'Destacados';
      case 'winery': return 'Bodega';
      case 'recommendations': return 'Recomendaciones';
      case 'category': return 'Categoría';
      case 'seasonal': return 'Temporada';
      default: return type;
    }
  };

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error al cargar las secciones: {error.message}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <BackButton />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SectionIcon />
          Gestión de Secciones
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={assignCategoriesToWines}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Asignar Categorías
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nueva Sección
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Orden de secciones:</strong> Usa las flechas ↑↓ para cambiar el orden en que aparecen las secciones en la página principal. 
          Las categorías se muestran automáticamente y no se pueden reordenar.
        </Typography>
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Orden</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Vinos</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sections.filter(section => section).map((section, index) => (
              <TableRow key={section.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '30px' }}>
                      {section?.order || index + 1}
                    </Typography>
                    {!section?.isCategory && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveUp(section)}
                          disabled={index === 0 || moveUpMutation.isPending || moveDownMutation.isPending}
                          sx={{ 
                            p: 0.5, 
                            minWidth: '24px', 
                            minHeight: '24px',
                            border: '1px solid #ccc',
                            '&:hover': {
                              backgroundColor: '#f5f5f5'
                            }
                          }}
                        >
                          <MoveUpIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDown(section)}
                          disabled={index === sections.length - 1 || moveUpMutation.isPending || moveDownMutation.isPending}
                          sx={{ 
                            p: 0.5, 
                            minWidth: '24px', 
                            minHeight: '24px',
                            border: '1px solid #ccc',
                            '&:hover': {
                              backgroundColor: '#f5f5f5'
                            }
                          }}
                        >
                          <MoveDownIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {section.name}
                    </Typography>
                    {section?.isCategory && (
                      <Chip
                        label="Categoría"
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {section.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getTypeLabel(section.type)}
                    color={getTypeColor(section.type)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getWinesBySection(section).length} / {section?.maxWines || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={section?.isActive ? 'Activa' : 'Inactiva'}
                    color={section?.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {!section?.isCategory && (
                    <Tooltip title="Editar sección">
                      <IconButton onClick={() => handleOpenDialog(section)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Gestionar vinos">
                    <IconButton onClick={() => handleOpenWineDialog(section)}>
                      <WineIcon />
                    </IconButton>
                  </Tooltip>
                  {!section?.isCategory && (
                    <Tooltip title="Eliminar sección">
                      <IconButton 
                        onClick={() => handleDeleteClick(section)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar sección */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSection ? 'Editar Sección' : 'Nueva Sección'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre de la sección"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de sección"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="featured">Destacados</option>
                  <option value="winery">Bodega</option>
                  <option value="recommendations">Recomendaciones</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Máximo de vinos"
                  value={formData.maxWines}
                  onChange={(e) => setFormData({ ...formData, maxWines: parseInt(e.target.value) })}
                  inputProps={{ min: 1, max: 12 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.isActive}
                      onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Activa"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editingSection ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para gestionar vinos de la sección */}
      <Dialog open={wineDialog.open} onClose={() => setWineDialog({ open: false, section: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          Gestionar Vinos - {wineDialog.section?.name}
          {wineDialog.section && wineDialog.section.isCategory && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              (Categoría automática)
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {wineDialog.section && wineDialog.section.isCategory && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Esta es una categoría. Los vinos se asignan automáticamente según su categoría y no se pueden modificar manualmente.
            </Alert>
          )}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Vinos en la sección
                  </Typography>
                  <List>
                    {wineDialog.section && getWinesBySection(wineDialog.section).map((wine) => (
                      <ListItem key={wine.id}>
                        <ListItemText
                          primary={wine.name}
                          secondary={`${wine.price}€`}
                        />
                        {!wineDialog.section.isCategory && (
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleRemoveWineFromSection(wine.id)}
                              color="error"
                            >
                              <RemoveWineIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    ))}
                    {(!wineDialog.section || getWinesBySection(wineDialog.section).length === 0) && (
                      <ListItem>
                        <ListItemText primary="No hay vinos en esta sección" />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Añadir vinos
                  </Typography>
                  <List>
                    {wineDialog.section && wines
                      .filter(wine => {
                        if (wineDialog.section.isCategory) {
                          // Para categorías, mostrar solo vinos que NO están en esa categoría
                          return wine.categoryId !== wineDialog.section.originalCategoryId;
                        } else {
                          // Para secciones personalizadas, mostrar vinos que NO están en wineIds
                          return !wineDialog.section?.wineIds?.includes(wine.id);
                        }
                      })
                      .map((wine) => (
                        <ListItem key={wine.id}>
                          <ListItemText
                            primary={wine.name}
                            secondary={`${wine.price}€`}
                          />
                          {!wineDialog.section.isCategory && (
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => handleAddWineToSection(wine.id)}
                                color="primary"
                              >
                                <AddWineIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                        </ListItem>
                      ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWineDialog({ open: false, section: null })}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para eliminar */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, section: null })}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que quieres eliminar la sección "{deleteDialog.section?.name}"?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, section: null })}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSections; 